import React, { useState, useRef, useEffect } from "react";
import "./packN.css";
import axios from "axios";
import TrainingQuestions from "../pack_N/trainingQuestions/trainingQuestions";
import PaymentN from "./paymentN/paymentN";
import "./packVip.css"; // ← NOUVEAU : thème VIP (chargé en dernier)
import {
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  ShoppingCart,
} from "lucide-react";

/* ── Benefit row: same card size, 2 lines of text by default,
   tap/click to expand and read the full content ── */
const InfoRow = ({ id, icon, label, value, expandedId, onToggle }) => {
  const isOpen = expandedId === id;

  const handleToggle = (e) => {
    const el = e.currentTarget;
    onToggle(id);
    // keep the expanded card visible inside the scroll area
    setTimeout(() => {
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 60);
  };

  return (
    <div
      className={`infoRow expandable ${isOpen ? "is-expanded" : ""}`}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleToggle(e);
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
    >
      <img src={icon} alt="" className="icon" />
      <div className="infoText">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
      <ChevronDown size={14} className="expandChevron" aria-hidden="true" />
    </div>
  );
};

const PopupdetailsPackN = ({ onClose, packName, packType }) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isVideoTrainingOpen, setIsVideoTrainingOpen] = useState(false);
  const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);
  const [isEndPopupOpen, setIsEndPopupOpen] = useState(false);
  const [maxWatchTime, setMaxWatchTime] = useState(0);
  const [isMessagePopupOpen, setIsMessagePopupOpen] = useState(false);
  const [isFreeSentOpen, setIsFreeSentOpen] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null); // which benefit card is expanded
  const [withoutIphone, setWithoutIphone] = useState(false); // pack version chosen by the user

  const videoTrainingRef = useRef(null);
  const userId = localStorage.getItem("userId");

  const toggleExpanded = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const checkQuizStatus = async () => {
    if (!userId) {
      setError("You must be logged in to proceed.");
      return false;
    }
    try {
      const response = await axios.get(
        `${API_URL}/check-quiz-status/${userId}/${details._id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      return response.data.canProceedToPayment;
    } catch (err) {
      console.error("Error checking quiz status:", err.message);
      setError("Failed to check quiz status. Please try again.");
      return false;
    }
  };

  const handleBuyClick = async () => {
    if (!userId) {
      setError("Please log in to continue.");
      return;
    }
    setBuyLoading(true);
    const canProceed = await checkQuizStatus();
    setBuyLoading(false);

    if (canProceed) {
      // free pack -> never show the payment form,
      // the request was already sent to the admin after the quiz
      if (details.isFree) setIsFreeSentOpen(true);
      else setShowPayment(true);
    } else {
      setIsInfoPopupOpen(true);
    }
  };

  const closeInfoPopup = () => {
    setIsInfoPopupOpen(false);
    setIsVideoTrainingOpen(true);
  };
  const closeVideoPopup = () => setIsVideoOpen(false);
  const closeVideoTrainingPopup = () => setIsVideoTrainingOpen(false);
  const closeEndPopup = () => setIsEndPopupOpen(false);
  const closeMessagePopup = () => setIsMessagePopupOpen(false);
  const closeFreeSentPopup = () => setIsFreeSentOpen(false);
  const closePaymentPopup = () => setShowPayment(false);

  const handleTimeUpdate = () => {
    const currentTime = videoTrainingRef.current.currentTime;
    if (currentTime > maxWatchTime + 0.5) {
      videoTrainingRef.current.currentTime = maxWatchTime;
      videoTrainingRef.current.play();
    } else {
      setMaxWatchTime(currentTime);
    }
  };

  const handleVideoEnd = () => setIsEndPopupOpen(true);

  const openTrainingQuestions = () => {
    setIsVideoOpen(false);
    setIsVideoTrainingOpen(false);
    setIsEndPopupOpen(false);
    setIsInfoPopupOpen(false);
    setIsMessagePopupOpen(false);
    setShowQuestions(true);
  };

  const getVideoType = (url) => {
    if (!url) return "video/mp4";
    return url.split(".").pop().toLowerCase() === "mov"
      ? "video/quicktime"
      : "video/mp4";
  };

  const getVideoUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `https://mwgtd.com${url}`;
  };

  const handleVideoError = (e, url) => {
    console.error("Video error:", url, e);
    fetch(url)
      .then((res) =>
        setVideoError(`Failed to load video. HTTP Status: ${res.status}.`),
      )
      .catch(() => setVideoError(`Network error or file not found: ${url}`));
  };

  useEffect(() => {
    const fetchPackDetails = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/packs/${encodeURIComponent(packName)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setDetails(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching pack details:", err.message);
        setError("Failed to fetch pack details. Please try again later.");
        setLoading(false);
      }
    };
    fetchPackDetails();
  }, [packName, API_URL]);

  if (loading) return <div className="packStateMsg">Loading…</div>;
  if (error) return <div className="packStateMsg packStateError">{error}</div>;

  const nameParts = details.packName?.split(" ") || [];
  const namePrefix = nameParts.slice(0, -1).join(" ");
  const nameSuffix = nameParts[nameParts.length - 1] || "";

  // "Without iPhone" exists only if the pack has price_Without_Iphone
  const hasWithoutIphone = Number(details.price_Without_Iphone) > 0;
  const isWithoutIphone = withoutIphone && hasWithoutIphone;
  const shownPrice = isWithoutIphone
    ? details.price_Without_Iphone
    : details.price;

  // NOUVEAU : thème VIP (packType vaut "VIP" ou "normal")
  const isVip = details.packType === "VIP";

  return (
    // NOUVEAU : la classe "vip" active le thème de packVip.css
    <div className={`packDetailsContentWrapper ${isVip ? "vip" : ""}`}>
      {showPayment ? (
        <PaymentN
          packName={packName}
          packType={packType}
          withoutIphone={isWithoutIphone}
          isOpen={true}
          onClose={closePaymentPopup}
        />
      ) : (
        <>
          {/* ── HEADER ── */}
          <div className="packHeader">
            <div
              className="packsCloseIcon"
              onClick={onClose}
              role="button"
              aria-label="Go back"
            >
              <ArrowLeft size={16} />
            </div>

            {!showQuestions && (
              <div className="headerTitleGroup">
                <div className="packInfoTitle">
                  {namePrefix && <span>{namePrefix} </span>}
                  <span className="goldText">{nameSuffix}</span>
                </div>
              </div>
            )}

            {showQuestions && <div className="trainingHeader" />}
          </div>

          {/* ── MAIN AREA ── */}
          <div className="packDetailsContent">
            {!showQuestions ? (
              <>
                <div className="priceCard">
                  <div className="priceCardLeft">
                    <div className="priceCardLabel">Pack price</div>
                    <div className="priceCardValue">
                      {shownPrice}
                      <span className="priceCardCurrency">
                        {details.currency}
                      </span>
                    </div>
                  </div>
                  <div className="priceCardIcon">
                    <img src="/images/details-packs/price.png" alt="" />
                  </div>
                </div>

                {/* With iPhone / Without iPhone */}
                {hasWithoutIphone && (
                  <div
                    className="variantRow"
                    role="radiogroup"
                    aria-label="Pack version"
                  >
                    <div
                      className={`variantPill ${!isWithoutIphone ? "active" : ""}`}
                      role="radio"
                      aria-checked={!isWithoutIphone}
                      tabIndex={0}
                      onClick={() => setWithoutIphone(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setWithoutIphone(false);
                        }
                      }}
                    >
                      With iPhone
                    </div>
                    <div
                      className={`variantPill ${isWithoutIphone ? "active" : ""}`}
                      role="radio"
                      aria-checked={isWithoutIphone}
                      tabIndex={0}
                      onClick={() => setWithoutIphone(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setWithoutIphone(true);
                        }
                      }}
                    >
                      Without iPhone
                    </div>
                  </div>
                )}

                <div className="sectionLabel">Benefits</div>

                <div className="benefitsScroll">
                  {details.balanceFacilityService && (
                    <InfoRow
                      id="facility"
                      icon="/images/details-packs/facility-service.png"
                      label="Balance Facility Service"
                      value={`${details.balanceFacilityService} ${details.currency}`}
                      expandedId={expandedId}
                      onToggle={toggleExpanded}
                    />
                  )}

                  <InfoRow
                    id="bonus"
                    icon="/images/details-packs/bonus-every-month.png"
                    label="Bonus Every Month"
                    value={details.bonusEveryMonthaff}
                    expandedId={expandedId}
                    onToggle={toggleExpanded}
                  />

                  <InfoRow
                    id="conversion"
                    icon="/images/details-packs/point-money.png"
                    label="Point Conversion"
                    value={details.monthlyBonus.valueaff}
                    expandedId={expandedId}
                    onToggle={toggleExpanded}
                  />

                  <InfoRow
                    id="gifts"
                    icon="/images/details-packs/gifts.png"
                    label="Gifts"
                    value={details.Gift}
                    expandedId={expandedId}
                    onToggle={toggleExpanded}
                  />

                  <InfoRow
                    id="withdrawal"
                    icon="/images/details-packs/withdrawal.png"
                    label="Withdrawal of Points"
                    value={`Every ${details.withdrawalDays} days`}
                    expandedId={expandedId}
                    onToggle={toggleExpanded}
                  />

                  {details.promotionalVideoUrl && (
                    <>
                      <div className="rowDivider" />
                      <div
                        className="infoRow videoRow"
                        onClick={() => setIsVideoOpen(true)}
                        role="button"
                        aria-label="Watch promotional video"
                      >
                        <img
                          src="/images/details-packs/video-pub.png"
                          alt=""
                          className="icon"
                        />
                        <div className="infoText">
                          <span className="label">Promo video</span>
                          <span className="value">Watch Pack Overview</span>
                        </div>
                        <ChevronRight size={16} className="videoChevron" />
                      </div>
                    </>
                  )}

                  {details.trainingVideoUrl && (
                    <div className="buyButtonRow">
                      <button
                        className="Buy-button"
                        onClick={handleBuyClick}
                        type="button"
                        disabled={buyLoading}
                      >
                        <span className="buyLabel">
                          <ShoppingCart size={16} />
                          {buyLoading ? "Checking…" : "Buy"}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <TrainingQuestions
                packName={packName}
                onProceedToPayment={() => setShowPayment(true)}
              />
            )}
          </div>
        </>
      )}

      {/* ══════════ MODALS ══════════ */}

      {isVideoOpen && details.promotionalVideoUrl && (
        <div className="videoOverlay" onClick={closeVideoPopup}>
          <div className="videoModal" onClick={(e) => e.stopPropagation()}>
            {videoError ? (
              <p className="packStateMsg packStateError">{videoError}</p>
            ) : (
              <video
                autoPlay
                controls
                onError={(e) =>
                  handleVideoError(e, getVideoUrl(details.promotionalVideoUrl))
                }
              >
                <source
                  src={getVideoUrl(details.promotionalVideoUrl)}
                  type={getVideoType(details.promotionalVideoUrl)}
                />
              </video>
            )}
          </div>
        </div>
      )}

      {isInfoPopupOpen && (
        <div className="infoOverlay" onClick={closeInfoPopup}>
          <div className="infoModal" onClick={(e) => e.stopPropagation()}>
            <p>
              You should watch this training video without skipping forward.
            </p>
            <button className="btn-popup-ok" onClick={closeInfoPopup}>
              OK, I understand
            </button>
          </div>
        </div>
      )}

      {isVideoTrainingOpen && (
        <div className="videoOverlay" onClick={closeVideoTrainingPopup}>
          <div className="videoModal" onClick={(e) => e.stopPropagation()}>
            {videoError ? (
              <p className="packStateMsg packStateError">{videoError}</p>
            ) : !details.trainingVideoUrl ? (
              <p className="packStateMsg">No training video available.</p>
            ) : (
              <video
                ref={videoTrainingRef}
                autoPlay
                controls
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnd}
                onError={(e) =>
                  handleVideoError(e, getVideoUrl(details.trainingVideoUrl))
                }
              >
                <source
                  src={getVideoUrl(details.trainingVideoUrl)}
                  type={getVideoType(details.trainingVideoUrl)}
                />
              </video>
            )}
          </div>
        </div>
      )}

      {isEndPopupOpen && (
        <div className="endVideoOverlay" onClick={closeEndPopup}>
          <div className="endVideoModal" onClick={(e) => e.stopPropagation()}>
            <p>Video has ended. Repeat to review or continue to the quiz.</p>
            <div className="btn-container">
              <button className="btn-nextStep-right" onClick={closeEndPopup}>
                ↩ Repeat
              </button>
              <button
                className="btn-nextStep-left"
                onClick={openTrainingQuestions}
              >
                Continue →
              </button>
            </div>
          </div>
        </div>
      )}

      {isMessagePopupOpen && (
        <div className="messageOverlay" onClick={closeMessagePopup}>
          <div className="messageModal" onClick={(e) => e.stopPropagation()}>
            <p>You must answer at least 3 out of 4 questions correctly.</p>
            <button className="btn-ok" onClick={closeMessagePopup}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* free pack already requested */}
      {isFreeSentOpen && (
        <div className="messageOverlay" onClick={closeFreeSentPopup}>
          <div className="messageModal" onClick={(e) => e.stopPropagation()}>
            <p>
              Your request for {details.packName} has already been sent to the
              admin. Please wait for their approval.
            </p>
            <button className="btn-ok" onClick={closeFreeSentPopup}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupdetailsPackN;
